import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import ClarityOneLayout from "@/components/clarity-one/ClarityOneLayout";
import { clarityOneLoginUrlWithRedirect } from "@shared/clarityOneAuthRedirect";
import { formatCustomerDeliveryDate } from "@shared/clarityOneDeliveryDate";
import { CLARITY_ONE_ASSISTANCE } from "@shared/clarityOneContact";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

function CustomerHelpAction({ subject }: { subject: string }) {
  return (
    <Button asChild variant="outline" className="min-h-10 border-[#E8E4DC]">
      <a
        href={`${CLARITY_ONE_ASSISTANCE.emailHref}?subject=${encodeURIComponent(subject)}`}
      >
        Contact Clarity
      </a>
    </Button>
  );
}

function formatQuantity(value: number, uom: string | null): string {
  const quantity = Number.isInteger(value)
    ? String(value)
    : String(Number(value.toFixed(3)));
  return [quantity, String(uom ?? "").trim()].filter(Boolean).join(" ");
}

export default function ClarityOneDeliveriesPage() {
  const [, setLocation] = useLocation();
  const { data: me, isLoading: meLoading } = trpc.clarityOneAuth.me.useQuery(
    undefined,
    {
      retry: false,
      refetchOnWindowFocus: false,
    }
  );
  const query = trpc.clarityOneRecords.listDeliveries.useQuery(undefined, {
    enabled: !!me,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const downloadPdf = trpc.clarityOneRecords.downloadDeliveryPdf.useMutation();
  const [pdfError, setPdfError] = useState(false);

  useEffect(() => {
    if (!meLoading && !me)
      setLocation(clarityOneLoginUrlWithRedirect("/deliveries"));
  }, [meLoading, me, setLocation]);

  if (meLoading || !me) {
    return (
      <ClarityOneLayout showNav={false}>
        <div className="flex justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-[#B8956E]" />
        </div>
      </ClarityOneLayout>
    );
  }

  const hasData = query.data != null;
  const sites = query.data?.sites ?? [];
  const empty = hasData && !query.isError && sites.length === 0;

  const download = async (siteId?: number) => {
    setPdfError(false);
    try {
      const pdf = await downloadPdf.mutateAsync(siteId ? { siteId } : {});
      const bytes = Uint8Array.from(atob(pdf.base64), c => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: pdf.mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = pdf.fileName;
      a.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setPdfError(true);
    }
  };

  return (
    <ClarityOneLayout showNav>
      <div
        className="min-w-0 overflow-x-hidden"
        data-testid="clarity-one-deliveries"
      >
        <h1 className="text-3xl font-light text-[#1A2744] tracking-tight mb-6">
          Deliveries
        </h1>
        {query.isError && !hasData ? (
          <div data-testid="clarity-one-deliveries-error">
            <p className="text-sm text-[#8B7355] mb-3">
              We could not load your deliveries.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                className="min-h-10 bg-[#1A2744] text-white"
                onClick={() => void query.refetch()}
              >
                Retry
              </Button>
              <CustomerHelpAction subject="Clarity One deliveries" />
            </div>
          </div>
        ) : null}
        {query.isError && hasData ? (
          <p className="mb-4 text-sm text-[#8B7355]">
            These deliveries may not be the latest. Retry to refresh.
          </p>
        ) : null}
        {query.isLoading && !hasData ? (
          <Loader2 className="h-6 w-6 animate-spin text-[#B8956E]" />
        ) : empty ? (
          <p
            className="text-sm text-[#8B7355]"
            data-testid="clarity-one-deliveries-empty"
          >
            No deliveries.
          </p>
        ) : hasData ? (
          <div className="space-y-8">
            <Button
              type="button"
              className="min-h-10 bg-[#1A2744] text-white"
              disabled={downloadPdf.isPending}
              onClick={() => void download()}
            >
              Download Delivery Record PDF
            </Button>
            {pdfError || downloadPdf.isError ? (
              <div data-testid="clarity-one-deliveries-pdf-error">
                <p className="text-sm text-[#8B7355] mb-2">
                  We could not prepare your delivery record.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void download()}
                  >
                    Retry
                  </Button>
                  <CustomerHelpAction subject="Clarity One delivery record" />
                </div>
              </div>
            ) : null}
            <div className="overflow-x-auto rounded-2xl border border-[#E8E4DC] bg-white">
              <table
                className="w-full min-w-[680px] text-left"
                data-testid="clarity-one-deliveries-table"
              >
                <thead className="border-b border-[#E8E4DC] bg-[#F8F6F1]">
                  <tr className="text-[11px] uppercase tracking-[0.12em] text-[#8B7355]">
                    <th scope="col" className="w-[18%] px-5 py-4 font-medium">
                      Date delivered
                    </th>
                    <th scope="col" className="w-[47%] px-5 py-4 font-medium">
                      Product
                    </th>
                    <th scope="col" className="w-[13%] px-5 py-4 font-medium">
                      Quantity
                    </th>
                    <th scope="col" className="w-[22%] px-5 py-4 font-medium">
                      Delivered to
                    </th>
                  </tr>
                </thead>
                {sites.flatMap(site =>
                  site.deliveries.map(delivery => (
                    <tbody
                      key={`${site.siteId}-${delivery.deliveryDate ?? "unknown"}`}
                      className="border-b border-[#E8E4DC] last:border-b-0"
                      data-testid="clarity-one-delivery-group"
                    >
                      {delivery.items.map((item, index) => (
                        <tr
                          key={`${item.description}-${index}`}
                          className="align-top"
                        >
                          {index === 0 ? (
                            <th
                              scope="rowgroup"
                              rowSpan={delivery.items.length}
                              className="whitespace-nowrap px-5 py-5 text-sm font-medium text-[#1A2744]"
                              data-testid="clarity-one-delivery-date"
                            >
                              {formatCustomerDeliveryDate(delivery.deliveryDate)}
                            </th>
                          ) : null}
                          <td className="min-w-0 px-5 py-5 text-sm text-[#1A2744]">
                            <p className="font-medium break-words whitespace-pre-line">
                              {item.description}
                            </p>
                            {item.serials.length ? (
                              <p className="mt-1 text-xs text-[#8B7355]">
                                Serial: {item.serials.join(", ")}
                              </p>
                            ) : null}
                            {(item.warranties ?? []).map(warranty => (
                              <p
                                key={`${warranty.serial}-${warranty.label}`}
                                className="mt-1 text-xs text-[#8B7355]"
                              >
                                {warranty.label}
                                {warranty.label === "Warranty live" &&
                                warranty.link === "/warranty" ? (
                                  <>
                                    {" · "}
                                    <Link
                                      href="/warranty"
                                      className="underline text-[#1A2744]"
                                      data-testid={`clarity-one-view-warranty-${warranty.serial}`}
                                    >
                                      View Warranty
                                    </Link>
                                  </>
                                ) : null}
                              </p>
                            ))}
                          </td>
                          <td className="px-5 py-5 text-sm font-medium text-[#1A2744]">
                            {formatQuantity(item.quantity, item.uom)}
                          </td>
                          {index === 0 ? (
                            <td
                              rowSpan={delivery.items.length}
                              className="px-5 py-5 text-sm text-[#566176]"
                            >
                              {site.siteAddress || "Site address unavailable"}
                            </td>
                          ) : null}
                        </tr>
                      ))}
                    </tbody>
                  ))
                )}
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </ClarityOneLayout>
  );
}
